<template>
  <v-layout row wrap>
    <v-flex xs6>
      <v-form class="px-2">
        <!-- Rule For -->
        <v-flex xs4>
          <v-select v-model="ruleInfo.ruleFor" :label="messages.label.FOR" :disabled="isEditMode" item-text="title"
            item-value="value" :items="ruleForList"></v-select>
        </v-flex>
        <template v-if="ruleInfo.ruleFor === 'account'">
          <div v-if="IS_POLARIS">
            <!-- Provider Group -->
            <v-flex sm4>
              <v-autocomplete v-model="selectedProviderGroup" :label="messages.label.PROVIDER_GROUP" item-text="title"
                item-value="title" :disabled="isEditMode" :loading="loadingProviderGroups" :items="providerGroups"
                return-object @change="retrieveSecondaryOwners(selectedProviderGroup.id)">
                <template v-slot:no-data>
                  <v-list-tile>
                    <v-list-tile-title>
                      No Provider groups
                    </v-list-tile-title>
                  </v-list-tile>
                </template>
              </v-autocomplete>
            </v-flex>
            <!-- Provider -->
            <v-flex xs4>
              <v-autocomplete v-model="selectedSecondaryOwner" :label="messages.label.PROVIDER_NAME"
                :loading="loadingSecondaryOwner" item-text="title" item-value="title" return-object
                :items="secondaryOwners">
              </v-autocomplete>
            </v-flex>
          </div>
          <div v-else>
            <!-- Sub Account -->
            <v-flex sm4 v-if="mpxSubAccounts.length > 0">
              <v-autocomplete v-model="selectedSubAccount" :label="messages.label.SUB_ACCOUNT" item-text="title"
                item-value="title" :loading="loadingSubAccounts" :disabled="isEditMode" :items="mpxSubAccounts"
                return-object>
              </v-autocomplete>
            </v-flex>
            <!-- Sub Account Title-->
            <v-flex xs4 v-else>
              <v-text-field v-model="subAccountTitle" :clearable="!isEditMode" v-validate="'required|max:100'"
                :counter="100" :disabled="isEditMode" :label="messages.label.SUB_ACCOUNT" required>
              </v-text-field>
            </v-flex>
          </div>
        </template>
        <!-- Rule Type -->
        <v-flex xs4>
          <v-select v-model="ruleInfo.type" item-text="title" item-value="value" :disabled="isEditMode"
            @change="handleRuleTypeChange" :label="messages.label.RULE_TYPE" :items="ruleTypeList"></v-select>
        </v-flex>
        <!-- Rule Title -->
        <v-flex xs10>
          <v-text-field v-model="ruleInfo.title" clearable v-validate="'required|max:100'"
            :error-messages="errors.collect('title')" data-vv-name="title" :counter="100"
            :label="messages.label.RULE_TITLE" required>
          </v-text-field>
        </v-flex>
        <!-- Rule Description -->
        <v-flex xs10>
          <v-textarea v-model="ruleInfo.description" clearable v-validate="'max:500'"
            :error-messages="errors.collect('description')" data-vv-name="description" :counter="500"
            :label="messages.label.RULE_DESCRIPTION" required>
          </v-textarea>
        </v-flex>
        <!-- Rule Enabled -->
        <v-flex xs4>
          <span class="subheading grey--text text--darken-2">{{
              messages.label.ENABLED
          }}</span>
          <v-switch v-model="ruleEnabled" color="primary" class="mt-0" :label="`${ruleEnabled.toString()}`"></v-switch>
        </v-flex>
      </v-form>
    </v-flex>
  </v-layout>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import {
  IS_POLARIS,
  VOD_SYCN_PREINGEST_DATA_SERVICE_URL
} from "@/store/constants/getter-types"
import { SECONDARY_OWNER_ANY } from "@/Shared/constants"
import { GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS } from "@/store/constants/action-types"

export default {
  name: "RuleInfo",
  $_veeValidate: {
    validator: "new"
  },
  computed: {
    ...mapState(["mpxParentAccount", "providerGroups", "mpxSubAccounts"]),
    ...mapGetters([IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL])
  },
  data() {
    return {
      ruleForList: MetadataRulesService.getForList(),
      ruleTypeList: MetadataRulesService.getRuleTypeList("rule"),
      isEditMode: false,
      ruleInfo: {
        ruleFor: "global",
        type: "",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      },
      loadingProviderGroups: false,
      selectedProviderGroup: {},
      selectedSubAccount: {},
      subAccountTitle: "",
      loadingSecondaryOwner: false,
      loadingSubAccounts: false,
      secondaryOwners: [],
      selectedSecondaryOwner: {}, // providers are secondary owners
      ruleEnabled: true, // NOTE: Using this outside of 'ruleInfo' as binding is not working
      dictionary: this.getValidationDictionary()
    }
  },
  methods: {
    ...mapActions([GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS]),
    /**
     * getValidationDictionary - Validation messages for vee-validate
     * @returns {object}
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          description: {
            max: "Description may not be greater than 500 characters"
          }
        }
      }
    },

    /**
     * resetData - Reset rule info data
     */
    resetData() {
      this.ruleInfo = {
        ruleFor: "global",
        type: "",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      }
      this.ruleEnabled = true
      this.isEditMode = false
    },

    /**
     * resetRuleInfo - Reset rule data and form
     */
    resetRuleInfo() {
      this.resetData()
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },

    /**
     * loadRuleInfo - update view with rule info data
     * @param {object} ruleData
     */
    loadRuleInfo(ruleData) {
      this.resetRuleInfo()
      if (!this.$lodash.isUndefined(ruleData)) {
        if (this.IS_POLARIS) {
          if (this.isOwnerParent(ruleData.ownerId)) {
            this.ruleInfo.ruleFor = "global"
          } else {
            this.ruleInfo.ruleFor = "account"
            this.retrieveProviderGroups(
              ruleData.ownerId,
              ruleData.secondaryOwner
            )
          }
        } else {
          if (this.isOwnerParent(ruleData.ownerId)) {
            if (ruleData.secondaryOwner !== SECONDARY_OWNER_ANY) {
              this.ruleInfo.ruleFor = "account"
              this.subAccountTitle = ruleData.secondaryOwner
            } else {
              this.ruleInfo.ruleFor = "global"
            }
          } else {
            this.ruleInfo.ruleFor = "account"
            this.retrieveSubAccounts(ruleData.ownerId)
          }
        }

        this.isEditMode = true
        this.ruleInfo.type = ruleData.type
        this.ruleInfo.title = ruleData.title
        this.ruleInfo.ownerId = ruleData.ownerId
        this.ruleInfo.secondaryOwner = ruleData.secondaryOwner
        this.ruleInfo.description = ruleData.description
        this.ruleInfo.enabled = !ruleData.disabled
      } else {
        this.isEditMode = false
        this.retrieveProviderGroups()
      }
    },

    /**
     * isOwnerParent - Check if the rule's owner is parent account
     */
    isOwnerParent(ownerId) {
      return this.mpxParentAccount.id === ownerId
    },

    /**
     * retrieveProviderGroups - retrieve provider groups
     */
    retrieveProviderGroups(
      currentProviderGroupAccountId,
      currentSecondaryOwner
    ) {
      // If the account is not Polaris, just return empty array
      if (!this.IS_POLARIS) {
        return
      }
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentProviderGroupAccountId)) {
            this.selectedProviderGroup = this.$lodash.find(
              this.providerGroups,
              { ownerId: currentProviderGroupAccountId }
            )
          } else {
            this.selectedProviderGroup = this.providerGroups[0]
          }
          return this.retrieveSecondaryOwners(
            this.selectedProviderGroup.id,
            currentSecondaryOwner
          )
        })
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingProviderGroups = false
        })
    },

    /**
     * retrieveSubAccounts - retrieve provider groups
     */
    retrieveSubAccounts(currentSubAccountId) {
      this.loadingSubAccounts = true
      this.GET_MPX_SUB_ACCOUNTS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentSubAccountId)) {
            this.selectedSubAccount = this.$lodash.find(this.mpxSubAccounts, {
              id: currentSubAccountId
            })
          } else {
            this.selectedSubAccount = this.mpxSubAccounts[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingSubAccounts = false
        })
    },

    /**
     * retrieveSecondaryOwners - Retrieve all providers from API service
     * @param {string} selectedSecondaryOwners
     */
    retrieveSecondaryOwners(providerGroupId, currentSecondaryOwner) {
      let queryObject = {}
      if (!this.$lodash.isUndefined(providerGroupId)) {
        queryObject["byProviderGroupId"] = providerGroupId
      }
      this.loadingSecondaryOwner = true
      return metadataRulesDataService
        .retrieveSecondaryOwners(
          queryObject,
          this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL
        )
        .then(secondaryOwners => {
          this.secondaryOwners = secondaryOwners
          if (!this.$lodash.isUndefined(currentSecondaryOwner)) {
            if (currentSecondaryOwner === SECONDARY_OWNER_ANY) {
              this.selectedSecondaryOwner = this.secondaryOwners[0]
            } else {
              this.selectedSecondaryOwner = this.$lodash.find(
                this.secondaryOwners,
                { title: currentSecondaryOwner }
              )
            }
          } else {
            this.selectedSecondaryOwner = this.secondaryOwners[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingSecondaryOwner = false
        })
    },

    /**
     * validate - validate form and dispatch event to notify
     */
    validate() {
      this.$validator.validateAll().then(result => {
        this.ruleInfo.enabled = this.ruleEnabled // Please see note at the definition of this property in 'data'
        if (this.$lodash.isNull(this.ruleInfo.description)) {
          this.ruleInfo.description = ""
        }
        // Update owner Id
        if (this.ruleInfo.ruleFor === "global") {
          this.ruleInfo.ownerId = this.mpxParentAccount.id
          this.ruleInfo.secondaryOwner = SECONDARY_OWNER_ANY
        } else {
          if (this.IS_POLARIS) {
            this.ruleInfo.ownerId = this.selectedProviderGroup.ownerId
            if (this.selectedSecondaryOwner.title === "Any") {
              this.ruleInfo.secondaryOwner = SECONDARY_OWNER_ANY
            } else {
              this.ruleInfo.secondaryOwner = this.selectedSecondaryOwner.title
            }
          } else {
            if (this.$lodash.isEmpty(this.selectedSubAccount)) {
              this.ruleInfo.ownerId = this.mpxParentAccount.id
              if (this.$lodash.isEmpty(this.subAccountTitle.trim())) {
                this.ruleInfo.secondaryOwner = SECONDARY_OWNER_ANY
              } else {
                this.ruleInfo.secondaryOwner = this.subAccountTitle
              }
            } else {
              this.ruleInfo.ownerId = this.selectedSubAccount.id
              this.ruleInfo.secondaryOwner = SECONDARY_OWNER_ANY
            }
          }
        }
        // Secondary owner

        MetadataRulesService.$emit(
          "event:ruleInfoValidation",
          result,
          this.ruleInfo
        )
      })
    },

    /**
     * Dispatch event to communicate rule type change
     */
    handleRuleTypeChange() {
      MetadataRulesService.$emit("event:ruleTypeChange", this.ruleInfo.type)
    }
  },
  mounted() {
    // Validator initialization
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages

    /**
     * Events
     */
    MetadataRulesService.$on("event:ruleDataRetrieved", this.loadRuleInfo)
    MetadataRulesService.$on("event:validateRuleInfo", this.validate)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:validateRuleInfo", this.validate)
    MetadataRulesService.$off("event:ruleDataRetrieved", this.loadRuleInfo)
  }
}
</script>
<style lang="sass"></style>



// WEBPACK FOOTER //
// src/MetadataRules/Rule/RuleInfo.vue